import React, { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Features } from '../../../types/features';

const Icon: FC<{ name: string }> = ({ name }) => {
  const props = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
  case 'home':
    return (
      <svg {...props}>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4M9 22V12h6v10" />
      </svg>
    );
  case 'calendar':
    return (
      <svg {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  case 'list':
    return (
      <svg {...props}>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    );
  case 'camera':
    return (
      <svg {...props}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  case 'user':
    return (
      <svg {...props}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  default:
    return null;
  }
};

export const BottomBar: FC<{ features: Features }> = ({ features }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  const tabPlateRecognizer = features.plateRecognizer
    ? [{ key: '/photo', title: 'Photo', icon: 'camera' }]
    : [];

  const tabs = [
    { key: '/', title: 'Accueil', icon: 'home' },
    { key: '/planning', title: 'Planning', icon: 'calendar' },
    { key: '/clients', title: 'Clients', icon: 'list' },
    ...tabPlateRecognizer,
    { key: '/me', title: 'Compte', icon: 'user' },
  ];

  const handleTabChange = (value: string) => {
    navigate(value);
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      background: 'var(--navy)',
      borderTop: '1px solid rgba(49,220,200,0.2)',
      boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
      height: '60px',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {tabs.map((tab) => {
        const isActive = pathname === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 2px',
              position: 'relative',
              WebkitTapHighlightColor: 'transparent',
              outline: 'none',
            }}
          >
            {/* Active indicator bar at top */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: '20%',
              right: '20%',
              height: '2px',
              borderRadius: '0 0 2px 2px',
              background: isActive ? 'var(--teal)' : 'transparent',
              transition: 'background 0.2s',
            }} />

            {/* Icon */}
            <div style={{
              color: isActive ? 'var(--teal)' : 'rgba(255,255,255,0.4)',
              transition: 'color 0.2s',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Icon name={tab.icon} />
            </div>

            {/* Label */}
            <span style={{
              fontSize: '10px',
              fontWeight: isActive ? 700 : 400,
              color: isActive ? 'var(--teal)' : 'rgba(255,255,255,0.35)',
              letterSpacing: '0.02em',
              transition: 'color 0.2s',
            }}>
              {tab.title}
            </span>
          </button>
        );
      })}
    </div>
  );
};
